import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import {
  PieChart, Pie, Cell,
  BarChart, Bar as RBar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

/* Formatear segundos → "5 min 30 seg", "1 h 20 min", etc. */
function formatearTiempo(seg) {
  if (seg == null) return '—';
  const s = Math.abs(Number(seg));
  if (s === 0)   return '0 seg';
  if (s < 60)    return `${Math.round(s)} seg`;
  if (s < 3600) {
    const min = Math.floor(s / 60);
    const sg  = Math.round(s % 60);
    return `${min} min${sg > 0 ? ' ' + sg + ' seg' : ''}`;
  }
  const h  = Math.floor(s / 3600);
  const mn = Math.floor((s % 3600) / 60);
  return `${h} h${mn > 0 ? ' ' + mn + ' min' : ''}`;
}

const COLORES = [
  '#e74c3c','#f39c12','#3498db','#2ecc71',
  '#9b59b6','#1abc9c','#e67e22','#27ae60','#34495e','#8e44ad',
];

const ROLES_PUEDEN_EXPORTAR = ['admin', 'super_admin', 'supervisor', 'monitoreo'];

export default function ReportesAlimentacion() {
  const { usuario } = useAuth();
  const [tipos, setTipos]               = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [usuarios, setUsuarios]         = useState([]);
  const [sectores, setSectores]         = useState([]);
  const [data, setData]                 = useState(null);
  const [loading, setLoading]           = useState(true);
  const [exportando, setExportando]     = useState(false);

  const puedeExportar = useMemo(
    () => ROLES_PUEDEN_EXPORTAR.includes(usuario?.rol),
    [usuario?.rol]
  );

  const [filtros, setFiltros] = useState({
    desde: new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10),
    hasta: new Date().toISOString().slice(0, 10),
    tipo_alerta_id: '',
    responsable_id: '',
    usuario_id: '',
    sector_id: '',
  });

  /* Cargar catálogos iniciales */
  useEffect(() => {
    const fincaId = localStorage.getItem('fincaActivaId') || '';
    Promise.all([
      api.get('/alimentacion/tipos-alerta'),
      api.get('/alimentacion/responsables'),
      api.get('/alimentacion/usuarios').catch(() => ({ data: [] })),
      fincaId ? api.get(`/sectores/finca/${fincaId}`) : Promise.resolve({ data: [] }),
    ]).then(([t, r, u, s]) => {
      setTipos(t.data);
      setResponsables(r.data);
      setUsuarios(Array.isArray(u.data) ? u.data : []);
      setSectores(Array.isArray(s.data) ? s.data : []);
    }).catch(err => console.error(err));
  }, []);

  /* Cuando cambia el usuario seleccionado → recargar sectores filtrados */
  useEffect(() => {
    const fincaId = localStorage.getItem('fincaActivaId') || '';
    if (!fincaId) return;

    if (!filtros.usuario_id) {
      api.get(`/sectores/finca/${fincaId}`)
        .then(r => setSectores(Array.isArray(r.data) ? r.data : []))
        .catch(() => setSectores([]));
      return;
    }

    api.get('/alimentacion/usuarios/' + filtros.usuario_id + '/sectores')
      .then(r => setSectores(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSectores([]));
  }, [filtros.usuario_id]);

  /* Si el sector seleccionado ya no existe en la lista filtrada, limpiarlo */
  useEffect(() => {
    if (!filtros.sector_id) return;
    if (!sectores.some(s => s.id === filtros.sector_id)) {
      setFiltros(f => ({ ...f, sector_id: '' }));
    }
  }, [sectores]);   // eslint-disable-line react-hooks/exhaustive-deps

  const cargar = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filtros).filter(([, v]) => v !== '')
      );
      const r = await api.get('/alimentacion/reporte', { params });
      setData(r.data);
    } catch (err) {
      console.error('Error cargando reporte:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [
    filtros.desde, filtros.hasta, filtros.tipo_alerta_id,
    filtros.responsable_id, filtros.usuario_id, filtros.sector_id,
  ]);

  const limpiarFiltros = () => {
    setFiltros({
      desde: new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10),
      hasta: new Date().toISOString().slice(0, 10),
      tipo_alerta_id: '',
      responsable_id: '',
      usuario_id: '',
      sector_id: '',
    });
  };

  const exportarExcel = async () => {
    setExportando(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filtros).filter(([, v]) => v !== '')
      );
      const res = await api.get('/alimentacion/reporte/exportar', {
        params,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_alimentacion_${filtros.desde}_a_${filtros.hasta}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando:', err);
      alert('Error al exportar. Revisa la consola.');
    } finally {
      setExportando(false);
    }
  };

  const donutData = useMemo(() => {
    if (!data?.porTipo) return [];
    return data.porTipo.map((p, i) => ({
      name:  p.nombre,
      value: p.total,
      color: p.color || COLORES[i % COLORES.length],
    }));
  }, [data]);

  const barData = useMemo(() => {
    if (!data?.tiemposHistograma) return [];
    return data.tiemposHistograma.map(t => ({
      rango: t.label,
      total: t.total,
    }));
  }, [data]);

  const coloresBar = ['#2ecc71', '#f1c40f', '#f39c12', '#e67e22', '#e74c3c'];

  const colorEstado = {
    abierta:    'bg-red-100 text-red-700',
    en_proceso: 'bg-amber-100 text-amber-700',
    resuelta:   'bg-green-100 text-green-700',
  };
  const etiquetaEstado = {
    abierta: 'Abierta', en_proceso: 'En proceso', resuelta: 'Resuelta',
  };

  return (
    <div className="p-6 space-y-5">
      {/* Encabezado + acciones */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">📊 Reportes de Alimentación</h1>
        <div className="ml-auto flex flex-wrap gap-2">
          <button
            onClick={cargar}
            className="bg-gradient-to-r from-indigo-500 to-purple-600
                       hover:from-indigo-600 hover:to-purple-700
                       text-white px-4 py-2 rounded"
          >
            🔍 Filtrar
          </button>

          {puedeExportar && (
            <button
              onClick={exportarExcel}
              disabled={exportando || !data}
              className="bg-emerald-600 hover:bg-emerald-700
                         disabled:opacity-50 disabled:cursor-not-allowed
                         text-white px-4 py-2 rounded flex items-center gap-2"
            >
              {exportando ? '⏳ Exportando…' : '📊 Exportar a Excel'}
            </button>
          )}

          <button
            onClick={limpiarFiltros}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
          >
            ↻ Limpiar
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-gray-50 border rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
            📅 Desde
          </label>
          <input type="date" value={filtros.desde}
                 onChange={e => setFiltros(f => ({ ...f, desde: e.target.value }))}
                 className="border rounded px-3 py-2 bg-white" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
            📅 Hasta
          </label>
          <input type="date" value={filtros.hasta}
                 onChange={e => setFiltros(f => ({ ...f, hasta: e.target.value }))}
                 className="border rounded px-3 py-2 bg-white" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
            🚨 Tipo Alerta
          </label>
          <select value={filtros.tipo_alerta_id}
                  onChange={e => setFiltros(f => ({ ...f, tipo_alerta_id: e.target.value }))}
                  className="border rounded px-3 py-2 bg-white">
            <option value="">Todos</option>
            {tipos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </div>

        {/* Responsable con buscador */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
            👤 Responsable
          </label>
          <div className="w-56">
            <ComboBox
              value={filtros.responsable_id}
              onChange={(v) => setFiltros(f => ({ ...f, responsable_id: v }))}
              options={responsables}
              placeholder="Buscar responsable…"
              getLabel={(o) => o.nombre + (o.rol ? ` (${o.rol})` : '')}
              getValue={(o) => o.id}
            />
          </div>
        </div>

        {/* Operador/Auxiliar con buscador */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
            ✍️ Ingresado por
          </label>
          <div className="w-56">
            <ComboBox
              value={filtros.usuario_id}
              onChange={(v) => setFiltros(f => ({ ...f, usuario_id: v, sector_id: '' }))}
              options={usuarios}
              placeholder="Buscar operador/auxiliar…"
              getLabel={(o) => o.nombre + (o.rol ? ` (${o.rol})` : '')}
              getValue={(o) => o.id}
            />
          </div>
        </div>

        {/* Sector */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
            📍 Sector
          </label>
          <select value={filtros.sector_id}
                  onChange={e => setFiltros(f => ({ ...f, sector_id: e.target.value }))}
                  className="border rounded px-3 py-2 bg-white"
                  disabled={sectores.length === 0}>
            <option value="">
              {filtros.usuario_id
                ? (sectores.length === 0 ? '— Sin sectores —' : 'Todos sus sectores')
                : 'Todos'}
            </option>
            {sectores.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="text-center text-gray-500 py-6">Cargando…</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard label="Total"        value={data.kpis.total}                     color="text-indigo-600" />
            <KpiCard label="Resueltas"    value={data.kpis.resueltas}                 color="text-green-600"  />
            <KpiCard label="Tiempo Prom." value={formatearTiempo(data.kpis.promedio)} color="text-amber-600"  />
            <KpiCard label="Más Rápido"   value={formatearTiempo(data.kpis.minimo)}   color="text-blue-600"   />
            <KpiCard label="Más Lento"    value={formatearTiempo(data.kpis.maximo)}   color="text-red-600"    />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">
                📈 Alertas por Tipo
              </h3>
              {donutData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      innerRadius={45}
                      paddingAngle={2}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {donutData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} alertas`, name]} />
                    <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center text-gray-400 text-sm py-16">
                  Sin datos para el rango
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">
                ⏱️ Distribución de Tiempos
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="rango" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => [`${value} alertas`, 'Cantidad']} />
                  <RBar dataKey="total" radius={[6, 6, 0, 0]}>
                    {barData.map((_, i) => (
                      <Cell key={i} fill={coloresBar[i] || '#888'} />
                    ))}
                  </RBar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow overflow-x-auto">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 text-sm">
                📋 Detalle
                <span className="text-gray-400 font-normal ml-2">
                  ({data.detalle.length} registros)
                </span>
              </h3>
              <span className="text-xs text-gray-500">
                {data.rango.desde} → {data.rango.hasta}
              </span>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3">Sector</th>
                  <th className="px-4 py-3">Responsable</th>
                  <th className="px-4 py-3">Ingresado por</th>
                  <th className="px-4 py-3">Alerta</th>
                  <th className="px-4 py-3"># de Equipo</th>
                  <th className="px-4 py-3">Hora rep.</th>
                  <th className="px-4 py-3">Hora resuelta</th>
                  <th className="px-4 py-3">Tiempo</th>
                  <th className="px-4 py-3">Diagnóstico</th>
                  <th className="px-4 py-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.detalle.length === 0 && (
                  <tr>
                    <td colSpan="12" className="px-4 py-8 text-center text-gray-400">
                      📭 No hay registros para los filtros seleccionados
                    </td>
                  </tr>
                )}
                {data.detalle.map((a, i) => {
                  const seg = Number(a.tiempo_segundos) || 0;
                  const cls = seg < 300  ? 'bg-green-100 text-green-700'
                            : seg < 1800 ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700';
                  return (
                    <tr key={a.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{a.fecha}</td>
                      <td className="px-4 py-2">{a.sector_nombre || '—'}</td>
                      <td className="px-4 py-2">{a.responsable_nombre || '—'}</td>
                      <td className="px-4 py-2">{a.usuario_nombre || '—'}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full"
                                style={{ background: a.alerta_color || '#999' }} />
                          {a.alerta_nombre}
                        </span>
                      </td>
                      <td className="px-4 py-2">{a.nro_tolva || '—'}</td>
                      <td className="px-4 py-2">{a.hora_reportada?.slice(0, 5)}</td>
                      <td className="px-4 py-2">{a.hora_alerta?.slice(0, 5)}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${cls}`}>
                          {formatearTiempo(seg)}
                        </span>
                      </td>
                      <td className="px-4 py-2">{a.diagnostico_nombre || '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${colorEstado[a.estado] || ''}`}>
                          {etiquetaEstado[a.estado] || a.estado}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !data && (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          No se pudo cargar el reporte. Revisa la consola.
        </div>
      )}
    </div>
  );
}

/* =========================================================
   ComboBox con búsqueda por texto
   ========================================================= */
function ComboBox({ value, onChange, options, placeholder, getLabel, getValue }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const ref               = useRef(null);

  const selected = options.find(o => getValue(o) === value);
  const display  = selected ? getLabel(selected) : query;

  const filtrados = useMemo(() => {
    const q = (query || '').toLowerCase().trim();
    if (!q) return options.slice(0, 30);
    return options.filter(o => getLabel(o).toLowerCase().includes(q)).slice(0, 30);
  }, [options, query, getLabel]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const elegir = (o) => {
    onChange(getValue(o));
    setQuery('');
    setOpen(false);
  };

  const limpiar = () => {
    onChange('');
    setQuery('');
    setOpen(true);
  };

  return (
    <div className="relative" ref={ref}>
      <input
        type="text"
        value={display}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value) onChange('');
        }}
        onFocus={() => setOpen(true)}
        className="w-full border rounded px-3 py-2 bg-white text-sm"
      />
      {value && (
        <button type="button" onClick={limpiar}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
          ✕
        </button>
      )}
      {open && filtrados.length > 0 && (
        <div className="absolute z-20 w-full bg-white border rounded mt-1 shadow-lg max-h-56 overflow-y-auto">
          {filtrados.map(o => (
            <div key={getValue(o)} onClick={() => elegir(o)}
                 className="px-3 py-2 hover:bg-sky-50 cursor-pointer text-sm">
              {getLabel(o)}
            </div>
          ))}
        </div>
      )}
      {open && filtrados.length === 0 && query && (
        <div className="absolute z-20 w-full bg-white border rounded mt-1 shadow-lg px-3 py-2 text-sm text-gray-400">
          Sin resultados
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-3 text-center">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-1">
        {label}
      </div>
    </div>
  );
}